/**
 * Resolving the --graph-* tokens into concrete strings for canvas.
 *
 * Canvas cannot consume var(), so this is the ONE place that reads computed
 * styles. Nothing else may duplicate it, or a hardcoded hex will creep in and
 * quietly break invariant 5.
 */
export interface GraphPalette {
  node: string;
  phantom: string;
  tag: string;
  attachment: string;
  current: string;
  currentRing: string;
  hover: string;
  stroke: string;
  link: string;
  linkHighlight: string;
  arrow: string;
  label: string;
  labelHalo: string;
  dimAlpha: number;
  enterMs: number;
}

const FALLBACK: GraphPalette = {
  node: "#9aa1ad",
  phantom: "#4c515b",
  tag: "#37c8a3",
  attachment: "#a08ad6",
  current: "#1fc19b",
  currentRing: "#6fe3c6",
  hover: "#e6e9ee",
  stroke: "#12141a",
  link: "#5e646f",
  linkHighlight: "#37c8a3",
  arrow: "#6b717c",
  label: "#a0a6b0",
  labelHalo: "#0f1115",
  dimAlpha: 0.18,
  enterMs: 420,
};

function num(raw: string, fallback: number): number {
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : fallback;
}

function ms(raw: string, fallback: number): number {
  const t = raw.trim();
  if (t.endsWith("ms")) return num(t, fallback);
  if (t.endsWith("s")) return num(t, fallback / 1000) * 1000;
  return fallback;
}

/**
 * Deterministic per-folder hue for the default "Color by folder" coloring.
 *
 * Like group colors, these are COMPUTED DATA, not component styling, which is
 * why they may be built here rather than read from a token (invariant 5's
 * group-color carve-out). Hashing the folder NAME keeps a folder's color
 * stable across sessions and devices; saturation/lightness are fixed so every
 * hue sits legibly on the dark canvas, matching the reference palette.
 */
export function folderColor(folderLower: string): string {
  let h = 2166136261;
  for (let i = 0; i < folderLower.length; i++) {
    h ^= folderLower.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const hue = ((h % 360) + 360) % 360;
  return `hsl(${hue} 62% 66%)`;
}

export function resolvePalette(): GraphPalette {
  if (typeof window === "undefined") return FALLBACK;
  const css = getComputedStyle(document.documentElement);
  const get = (name: string, fallback: string): string => {
    const v = css.getPropertyValue(name).trim();
    return v || fallback;
  };
  return {
    node: get("--graph-node", FALLBACK.node),
    phantom: get("--graph-node-phantom", FALLBACK.phantom),
    tag: get("--graph-node-tag", FALLBACK.tag),
    attachment: get("--graph-node-attachment", FALLBACK.attachment),
    current: get("--graph-node-current", FALLBACK.current),
    currentRing: get("--graph-node-current-ring", FALLBACK.currentRing),
    hover: get("--graph-node-hover", FALLBACK.hover),
    stroke: get("--graph-node-stroke", FALLBACK.stroke),
    link: get("--graph-link", FALLBACK.link),
    linkHighlight: get("--graph-link-highlight", FALLBACK.linkHighlight),
    arrow: get("--graph-arrow", FALLBACK.arrow),
    label: get("--graph-label", FALLBACK.label),
    labelHalo: get("--graph-label-halo", FALLBACK.labelHalo),
    dimAlpha: num(get("--graph-dim-alpha", "0.18"), FALLBACK.dimAlpha),
    enterMs: ms(get("--graph-dur-enter", "420ms"), FALLBACK.enterMs),
  };
}
